import { Link, useLocation } from 'react-router';
import { Async } from '../../components/Async.tsx';
import { DataTable, type Column, type TotalsRow } from '../../components/DataTable.tsx';
import { EmptyState, Panel } from '../../components/States.tsx';
import { formatDuration, formatNumber, formatPercent } from '../../lib/format.ts';
import { useLocale, useT } from '../../lib/i18n.tsx';
import { usePageTitle } from '../../lib/page-title.ts';
import { useTeam, type TeamResponse } from '../../lib/queries.ts';
import { carryOver, useDashboardParams } from '../../lib/url-state.ts';
import { Controls } from '../controls/Controls.tsx';
import { CallHighlightsPanel } from './CallHighlightsPanel.tsx';
import { SellerCallsPanel } from './SellerCallsPanel.tsx';

type Row = TeamResponse['data']['rows'][number];

export function TeamPage() {
  const t = useT();
  const locale = useLocale();
  usePageTitle(t.team.title);
  const { search } = useLocation();
  const [params] = useDashboardParams();
  const team = useTeam(params);
  const units = { minutes: t.units.minutes, hours: t.units.hours };
  const n = (value: number): string => formatNumber(value, locale);

  return (
    <>
      <h1 className="text-2xl font-bold tracking-tight">{t.team.title}</h1>
      <Controls meta={team.data?.meta} compare={false} />
      <Panel title={t.team.leaderboard}>
        <p className="mb-3 text-sm text-muted-foreground">{t.team.neutralNote}</p>
        <Async query={team}>
          {({ data }) => {
            const rows: Row[] = [...data.rows, ...(data.unassigned ? [data.unassigned] : [])];
            if (rows.length === 0)
              return <EmptyState title={t.common.noData} description={t.team.empty} />;
            const attribution = (key: string): string | undefined => {
              const rule = data.attribution[key];
              return rule
                ? (t.team.attribution[rule as keyof typeof t.team.attribution] ?? rule)
                : undefined;
            };
            const columns: Column<Row>[] = [
              {
                id: 'name',
                header: t.team.columns.name,
                sortValue: (r) => r.salesperson.fullName ?? '',
                cell: (r) =>
                  r.salesperson.id ? (
                    <Link
                      className="text-primary underline"
                      to={{
                        pathname: `/team/${r.salesperson.id}`,
                        search: carryOver(new URLSearchParams(search)),
                      }}
                    >
                      {r.salesperson.fullName}
                    </Link>
                  ) : (
                    t.team.unassigned
                  ),
              },
              {
                id: 'containers',
                header: t.team.columns.containersSold,
                help: attribution('containersSold'),
                align: 'right',
                sortValue: (r) => r.containersSold,
                cell: (r) => n(r.containersSold),
              },
              {
                id: 'deals',
                header: t.team.columns.dealsWon,
                help: attribution('dealsWon'),
                align: 'right',
                sortValue: (r) => r.dealsWon,
                cell: (r) => n(r.dealsWon),
              },
              {
                id: 'assigned',
                header: t.team.columns.leadsAssigned,
                help: attribution('leadsAssigned'),
                align: 'right',
                sortValue: (r) => r.leadsAssigned,
                cell: (r) => n(r.leadsAssigned),
              },
              {
                id: 'contacted',
                header: t.team.columns.leadsContacted,
                help: attribution('leadsContacted'),
                align: 'right',
                sortValue: (r) => r.leadsContacted,
                cell: (r) => n(r.leadsContacted),
              },
              {
                id: 'qualified',
                header: t.team.columns.leadsQualified,
                help: attribution('leadsQualified'),
                align: 'right',
                sortValue: (r) => r.leadsQualified,
                cell: (r) => n(r.leadsQualified),
              },
              {
                id: 'planned',
                header: t.team.columns.visitsPlanned,
                align: 'right',
                sortValue: (r) => r.visitsPlanned,
                cell: (r) => n(r.visitsPlanned),
              },
              ...(data.visitAvailability.completed
                ? [
                    {
                      id: 'completed',
                      header: t.team.columns.visitsCompleted,
                      align: 'right' as const,
                      sortValue: (r: Row) => r.visitsCompleted,
                      cell: (r: Row) => n(r.visitsCompleted),
                    },
                  ]
                : []),
              {
                id: 'conversion',
                header: t.team.columns.cohortConversion,
                align: 'right',
                sortValue: (r) => r.cohort.overallConversion,
                cell: (r) => formatPercent(r.cohort.overallConversion, locale),
              },
              {
                id: 'response',
                header: t.team.columns.firstResponse,
                align: 'right',
                sortValue: (r) => r.firstResponse.medianMinutes,
                cell: (r) => formatDuration(r.firstResponse.medianMinutes, locale, units),
              },
              {
                id: 'ontime',
                header: t.team.columns.followUpOnTime,
                align: 'right',
                sortValue: (r) => r.followUpOnTime.rate,
                cell: (r) => formatPercent(r.followUpOnTime.rate, locale),
              },
              {
                id: 'overdue',
                header: t.team.columns.followUpsOverdue,
                align: 'right',
                sortValue: (r) => r.followUpsOverdue,
                cell: (r) => n(r.followUpsOverdue),
              },
            ];
            const totals: TotalsRow = {
              label: t.team.totals,
              cells: {
                containers: n(data.totals.containersSold),
                deals: n(data.totals.dealsWon),
                assigned: n(data.totals.leadsAssigned),
                contacted: n(data.totals.leadsContacted),
                qualified: n(data.totals.leadsQualified),
                planned: n(data.totals.visitsPlanned),
                completed: n(data.totals.visitsCompleted),
                conversion: formatPercent(data.totals.cohort.overallConversion, locale),
                response: formatDuration(data.totals.firstResponse.medianMinutes, locale, units),
                ontime: formatPercent(data.totals.followUpOnTime.rate, locale),
                overdue: n(data.totals.followUpsOverdue),
              },
            };
            return (
              <DataTable
                caption={t.team.leaderboard}
                columns={columns}
                rows={rows}
                rowKey={(r) => r.salesperson.id ?? 'unassigned'}
                defaultSort={{ id: 'containers', direction: 'desc' }}
                totals={totals}
              />
            );
          }}
        </Async>
      </Panel>
      <SellerCallsPanel params={params} />
      <CallHighlightsPanel params={params} />
    </>
  );
}
